import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';
import { Formik } from 'formik';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import firstWordCapitalized from 'common/util/firstWordCapitalized';

import { SR_API_ACTION_ADMIN_MESSAGE_TENANT } from 'api/actionTypes';
import { DATA_TABLE_SOURCE_FRONT_AUTO_TEXTS } from 'common/dataTableConstants';
import {
  FRONT_TEXT_CENTER_TASK_REQUEST_TENANT_CONFIRMATION_FOR_TOUR_DATE,
  FRONT_TEXT_CENTER_STATUS_CREATED,
  FRONT_TEXT_CENTER_STATUS_SCHEDULED,
  FRONT_TEXT_CENTER_STATUS_ACTIVE,
} from 'common/frontTextCenter/frontTextCenterConstants';

import SrFormikField from 'client/components/SrFormikField';
import ButtonDeprecated, { BUTTON_THEME_BLUE } from 'client/components/ButtonDeprecated';

import postWithState from 'client/actions/postWithState';
import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';
import {
  actionDataTableLoadItems,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';
import withApiState from 'client/helpers/withApiState';

const formFields = [
  {
    id: 'message',
    type: 'long_text',
    validations: ['required'],
    properties: {
      placeholder: 'Message...',
    },
  },
];

@withApiState({
  messageTenantAsAdminApiState: {
    action: SR_API_ACTION_ADMIN_MESSAGE_TENANT,
  },
})
class SrAdminMessageTenant extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    listingId: PropTypes.number.isRequired,
    tour: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    messageTenantAsAdminApiState: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      isSent: false,
    };
  }

  _onSendMessage = (values) => {
    const { dispatch, tour, listingId, onClose } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_MESSAGE_TENANT,
      payload: {
        ...values,
        listingId,
        tourId: tour.id,
      },
      onSuccess: () => {
        this.setState({ isSent: true });
        setTimeout(onClose, 1000);
      },
    });
  };

  _onValidateNotActiveAutoText = (phone) => {
    const { dispatch } = this.props;
    return new Promise((resolve, reject) => {
      const dataTableSettings = {
        dataSource: DATA_TABLE_SOURCE_FRONT_AUTO_TEXTS,
        viewId: `user-phone-${phone}`,
        count: 2,
        filter: {
          phone,
          frontTextTaskId: FRONT_TEXT_CENTER_TASK_REQUEST_TENANT_CONFIRMATION_FOR_TOUR_DATE,
          status: [
            FRONT_TEXT_CENTER_STATUS_CREATED,
            FRONT_TEXT_CENTER_STATUS_SCHEDULED,
            FRONT_TEXT_CENTER_STATUS_ACTIVE,
          ],
        },
      };

      dispatch(actionInitializeDataTableState, dataTableSettings);
      dispatch(actionDataTableLoadItems, {
        ...dataTableSettings,
        onSuccess: ({ items }) => {
          const frontAutoText = items[0];
          if (frontAutoText) {
            const now = momentWithAustinTimezone();
            const nextReminderDate = frontAutoText.reminderDates.find((reminderDate) =>
              momentWithAustinTimezone(reminderDate).isAfter(now),
            );

            let errorMessage = 'This phone number already has an autotext active.';
            if (nextReminderDate) {
              const nextReminderDateFormatted = momentWithAustinTimezone(nextReminderDate).calendar(
                now,
                {
                  sameDay: 'h:mma [today]',
                  nextDay: 'h:mma [tomorrow]',
                  nextWeek: 'h:mma dddd',
                },
              );
              errorMessage = `This phone number already has an autotext sending at ${nextReminderDateFormatted}.`; // eslint-disable-line max-len
            }

            // eslint-disable-next-line prefer-promise-reject-errors
            reject(
              `${errorMessage} Please go to Front to check the situation and to send them a text.`,
            );
          } else {
            resolve();
          }
        },
      });
    });
  };

  render() {
    const { tour, messageTenantAsAdminApiState } = this.props;
    const { isSent } = this.state;
    const tourStartDateFormatted = momentWithAustinTimezone(tour.startDate).calendar(null, {
      sameDay: 'h:mma [today]',
      nextDay: 'h:mma [tomorrow]',
      nextWeek: 'h:mma dddd',
    });

    return (
      <Portal>
        <div className="adminModal messageTenant">
          <div className="backdrop" onClick={this.props.onClose}>
            <div className="form" onClick={(event) => event.stopPropagation()}>
              <h1 className="title">tenant occupied outreach</h1>
              <div className="tourDate">
                <span>For </span>
                <span className="strong">{firstWordCapitalized(tour.user.name)}'s</span>
                <span> tour at </span>
                <span className="strong">{tourStartDateFormatted}</span>
              </div>
              <div>
                <Formik
                  enableReinitialize
                  validateOnChange={false}
                  initialValues={createInitialValues({
                    formFields: formFields,
                    defaultValues: {},
                  })}
                  onSubmit={this._onSendMessage}
                  validate={createFormValidationFn(formFields)}
                  render={(formikBag) => {
                    return (
                      <div>
                        <SrFormikField
                          formField={{
                            id: 'phone',
                            type: 'phone',
                            validations: ['phone'],
                            properties: {
                              placeholder: 'Tenant Phone Number...',
                              validate: this._onValidateNotActiveAutoText,
                            },
                          }}
                          formikBag={formikBag}
                        />
                        {formFields.map((formField) => (
                          <SrFormikField
                            key={formField.id}
                            formField={formField}
                            formikBag={formikBag}
                          />
                        ))}

                        <ButtonDeprecated
                          className="save"
                          theme={BUTTON_THEME_BLUE}
                          onClick={formikBag.handleSubmit}
                          disabled={
                            !formikBag.isValid || isSent || messageTenantAsAdminApiState.inProgress
                          }
                        >
                          {isSent ? 'sent!' : 'send message to tenant'}
                        </ButtonDeprecated>
                      </div>
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Portal>
    );
  }
}

SrAdminMessageTenant.displayName = 'SrAdminMessageTenant';

export default SrAdminMessageTenant;
